<template>
  <div class="card">
    <div class="card__image">
      <div v-for="(image, i) in card.images" :key="i" class="card__image__wrapper">
        <img :src="`${image.smallUrl}`" alt="" />
      </div>

      <button class="card__image__mask" @click="fullView">
        <i class="fas fa-search-plus"></i>
      </button>
    </div>

    <div class="card__text">
      <div class="card__text__user">
        <p>{{ card.client.firstName }} {{ card.client.lastName }}</p>
        <p>{{ prettyDate(card.createdAt) }}</p>
      </div>

      <div class="card__text__expert">
        <p v-if="card.expert !== null">
          {{ $t('estimation.assigned_to') }}: {{ expertName(card.expert) }}
        </p>
        <button class="link" @click="expertModalShow = true">
          {{ $t('estimation.change_assigned_to') }}
        </button>
      </div>
    </div>

    <div v-if="status === 'pending'">
      <div class="card__actions">
        <button
          class="button button--danger"
          @click="rejectEstimation(rejectReason.unusable_photo)"
        >
          {{ $t('estimation.unusable_photo') }}
        </button>

        <ButtonDropdown :options="buttons" @action="rejectEstimation" />
        <div class="input__number">
          <input type="number" min="1" v-model="estimationValue" />
        </div>
        <button
          class="button button--valid"
          type="button"
          @click="estimationValidation"
        >
          {{ $t('estimate') }}
        </button>
        <textarea
          v-model="expertComment"
          class="input__message"
          :placeholder="$t('estimation.add_an_expert_comment')"
        ></textarea>
      </div>
    </div>

    <div class="card__actions" v-if="status === 'sent'">
      <div class="card__actions__title">
        {{ $t('estimation.for') }} {{ toCurrency(card.estimationValue) }}
      </div>
      <div class="card__actions__content">
        <i class="icon--blue fas fa-arrow-right"></i>
        {{ $t('estimation.estimatedAt') }} {{ prettyFullDate(card.estimatedAt) }}
      </div>
    </div>

    <div v-if="status === 'pending_transaction'">
      <div class="card__actions">
        <div class="card__actions__title">
          {{ $t('estimation.for') }} {{ toCurrency(card.estimationValue) }}
        </div>
        <button
          v-if="isAdmin"
          type="button"
          class="button button--valid"
          @click="submitValidation"
        >
          {{ $t('transaction.validation') }}
        </button>
      </div>
    </div>

    <div class="card__actions" v-if="status === 'validated_transaction'">
      <div class="card__actions__title">
        {{ $t('estimation.for') }} {{ toCurrency(card.estimationValue) }}
      </div>
      <div class="card__actions__content">
        <i class="icon--blue fas fa-arrow-right"></i>
        {{ $t('transaction.validatedAt') }} {{ prettyFullDate(card.validatedAt) }}
      </div>
    </div>

    <ExpertListModal
      v-if="expertModalShow"
      :selected-expert="card.expert"
      @closeModal="expertModalShow = false"
      @updateAssignation="submitAssignation"
    />
  </div>
</template>

<script>
import { ButtonDropdown } from './common';
import ExpertListModal from './ExpertListModal';
import {
  formatDate,
  formatNumber,
  expertName,
  EstimationComponent,
} from '../mixins';
import { mapGetters } from 'vuex';

export default {
  components: { ButtonDropdown, ExpertListModal },
  mixins: [formatDate, formatNumber, expertName, EstimationComponent],

  computed: {
    ...mapGetters('user', ['currentUser', 'isAdmin']),
  },

  props: {
    card: {
      type: Object,
      required: true,
    },
    status: {
      type: String,
      default: 'pending',
    },
  },

  data() {
    return {
      expertModalShow: false,
      buttons: [
        {
          title: 'estimation.ordinary_money',
          action: 'ordinary_money',
          buttonClass: 'button button--danger button--list-danger',
        },
        {
          title: 'estimation.counterfeit_money',
          action: 'counterfeit_money',
          buttonClass: 'button button--danger button--list-danger',
        },
        {
          title: 'estimation.bullion_coin',
          action: 'bullion_coin',
          buttonClass: 'button button--danger button--list-danger',
        },
      ],
    };
  },

  methods: {
    ...mapGetters('app', ['setToaster']),
    async estimationValidation() {
      try {
        const response = await this.submitEstimation({
          estimationValue: this.estimationValue,
          estimation: this.card,
          expertComment: this.expertComment,
        });

        if (response.error !== undefined) {
          this.setToaster([
            {
              error: 'errors.estimation.positive_value',
              time: Date.now(),
            },
          ]);
          return;
        }

        this.$emit('updateEstimation');
      } catch (e) {
        this.setToaster([
          {
            error: 'errors.estimation.submit',
            time: Date.now(),
          },
        ]);
      }
    },

    async rejectEstimation(rejectReason) {
      try {
        const expertComment = this.expertComment;
        await this.updateEstimation(this.card['@id'], {
          rejectReason,
          expertComment,
        });
        this.$emit('updateEstimation');
      } catch (e) {
        this.setToaster([
          {
            error: 'errors.estimation.reject',
            time: Date.now(),
          },
        ]);
      }
    },

    async submitAssignation(expert) {
      await this.updateAssignation(this.card['@id'], expert);
      this.$emit('updateEstimation');
      this.expertModalShow = false;
    },

    fullView() {
      this.$emit('fullView', this.card.id);
    },

    async submitValidation() {
      if (this.isAdmin) {
        await this.updateEstimation(this.card['@id'], {
          transactionValidated: true,
        });
        this.$emit('updateEstimation');
      } else {
        this.setToaster([
          {
            error: 'errors.transaction.not_admin',
            time: Date.now(),
          },
        ]);
      }
    },
  },
};
</script>
